/**
 * log-edit-receipt-plan.rules.ts
 *
 * Turns the items editor's EDITED check-off rows back into handoff-18 §3.1's
 * receipt changes: sparse `updated` patches, `added` rows, and `deletedIds`.
 * The inverse of `log-edit-line-items.rules.ts`, and the last piece between a
 * line-item check-off and the `PUT`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT A FIELD-BY-FIELD COPY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A check-off row says "line 3 arrived, 4 of them". The ledger holds ROWS WITH
 * IDS, and one PO line can own SEVERAL of them — a ragged line writes one row per
 * part. So "line 3 went 6 → 4" has no single id to write to until something
 * decides which rows the new state corresponds to.
 *
 * The decision this file makes: build the rows the SUBMIT would write for the
 * edited line (`buildReceiptRows`, the same rule the mobile flow uses), then
 * reconcile that target against the rows the log actually has, pairing them in
 * order. Pairs become sparse patches of ONLY the fields that differ; surplus
 * stored rows are deleted; surplus target rows are added.
 *
 * Reusing the submit's builder is the point. It means an edited log's ledger ends
 * up in the same shape a fresh submit of the same delivery would produce, rather
 * than in a second shape that only the edit path can create — and the ragged
 * one-row-per-part rule, the Complete-sends-the-outstanding-delta rule, and the
 * human-close rule are all obeyed without being restated here.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT DIFFS AGAINST THE SEED, NOT AGAINST THE LEDGER
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A line the user never touched must produce NOTHING, and "the target rows happen
 * to equal the stored rows" is not a reliable way to get there. Here is the case
 * that breaks it:
 *
 *   Log A completes line 3 (10 expected, 0 received before it) and stores
 *   `quantityReceived: 10`. Later, log B books 3 more against the same line, so
 *   the PO rollup reads 13 and log A's prior ledger is now 3. Reopen log A: a
 *   Complete tap sends the outstanding delta, which is now `10 − 3 = 7`. Target 7
 *   against stored 10 looks like an edit, and log A silently loses 3 units —
 *   because log B exists, not because anybody changed log A.
 *
 * So a line is reconciled ONLY when the user's own edit moved it. The comparison
 * is edited-row-versus-SEEDED-row on the four fields the check-off owns
 * (`status`, `receivedQty`, `partsReceived`, `completesLine`); everything else is
 * left exactly as stored. This is the same "only what the user touched" discipline
 * `ReceiptRowEdits` enforces one level down, applied one level up where the
 * quantity arithmetic can drift underneath it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FIELDS THIS FILE REFUSES TO TOUCH, AND WHY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A patch is dangerous in proportion to what it carries that the user did not
 * change (§3.1). Only three fields are ever diffed — `quantityReceived`,
 * `receiptStatus`, `completesLine` — plus `itemDescription` on a ragged line,
 * where the description IS the part the user typed. Everything else is excluded
 * BY CONSTRUCTION, not by being carefully compared:
 *
 * - **Damage** (`isDamaged`, `damagedQuantity`, `damageNote`,
 *   `damageDisposition`) is never emitted. This editor does not edit damage, and
 *   a `damagedQuantity` of 0 makes the backend re-derive `isDamaged: false`,
 *   erasing a damage report while its photos stay attached to the row. Damage is
 *   still fed INTO the builder, because `applyDamage` is what makes the target
 *   quantity agree with the stored one — but it never comes back out.
 * - **`notes`** is not diffed. The check-off has no notes control, so the stored
 *   note is not this screen's to rewrite — and the target rows carry `null`
 *   there, which would read as "clear it" if it were treated as an edit.
 * - **`itemNumber` / `unitOfMeasure` / `quantityOrdered`** come from the PO, not
 *   from the user. If the ERP's copy has drifted since the log was written,
 *   diffing them would silently rewrite stored history nobody asked to change.
 * - **`poLineSequence`** never moves. A row belongs to the line it was written
 *   against; "the same arrival, on a different line" is a delete plus an add.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE THING A PATCH CANNOT DO
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This endpoint writes only NON-NULL fields, so a patch cannot clear a value back
 * to `null` (§3.1). That is not academic here: converting a countable line to a
 * ragged one needs `quantityReceived: null`, and a human-close needs
 * `receiptStatus: null`.
 *
 * Where a pair needs a null-clear, the row is REPLACED — deleted and re-added —
 * because a delete followed by an insert can express what a patch cannot, and the
 * backend applies deletes before adds.
 *
 * Except on a DAMAGED row. A delete is refused with a `400` when the row has
 * damage photos attached (§3.1), and even when it succeeds it discards a damage
 * report this screen has no way to re-send. So a damaged row that would need a
 * null-clear is left alone and reported in {@link ReceiptPlan.unsupported} for the
 * UI to put in front of the user. Silently dropping the change, or silently
 * deleting the damage, are the two outcomes this exists to prevent.
 */

import { CheckableLineItem, LineDamage } from '../../../service/receiving-mobile/log-entry-draft.store';
import { ReceivingLogEditReceipt } from '../../../models/logs/ReceivingLogDetails';
import { ReceiptSubmitRow } from '../../../models/logs/ReceiptSubmitRow';
import { ReceiptRowEdits } from './receiving-log-save.rules';
import { buildReceiptRows } from './receipt-rows.rules';
import { isRaggedLine, splitRaggedParts } from './line-receipts.rules';

/**
 * The receipt changes an edited check-off implies — the three
 * `LogEditWorkingState` fields that drive `receipts` on the request, so a caller
 * assigns them straight across and the save path needs no new behaviour, plus
 * anything the endpoint cannot express.
 */
export interface ReceiptPlan {
  /** Sparse per-id patches. Only rows with a real change appear. */
  receiptEdits: Map<number, ReceiptRowEdits>;
  /** Brand-new rows, in the submit's own shape. Still subject to `validateAddedReceiptRows`. */
  addedReceipts: ReceiptSubmitRow[];
  deletedReceiptIds: number[];
  /**
   * Edits that cannot be sent, each already phrased for the user.
   *
   * Non-empty means the screen must say so rather than save quietly — every entry
   * is a change the user made that this endpoint has no way to express, and the
   * alternative to reporting it is a save that silently keeps the old value.
   */
  unsupported: string[];
}

/** An empty plan — what an untouched screen produces. */
export function emptyReceiptPlan(): ReceiptPlan {
  return { receiptEdits: new Map(), addedReceipts: [], deletedReceiptIds: [], unsupported: [] };
}

/** The check-off fields a user can move. Anything else on the row is derived or from the PO. */
type EditableLineFields =
  Pick<CheckableLineItem, 'status' | 'receivedQty'>
  // `quantity` is REQUIRED on the underlying PO-line type but is only meaningful
  // here for off-PO rows, so it is relaxed to optional rather than forcing every
  // caller to supply a field the comparison may not even read.
  & Partial<Pick<CheckableLineItem, 'partsReceived' | 'completesLine' | 'quantity' | 'isOffPo'>>;

/**
 * Whether the user moved this line at all.
 *
 * Compared field by field rather than by reference: the check-off UI replaces rows
 * with spread copies on every tap, so reference equality would report a change on
 * a line the user merely looked at.
 *
 * `quantity` counts ONLY on an off-PO row, where it holds the arrival. On a PO line
 * it holds the EXPECTED amount, which comes from the PO and is not the user's to
 * edit — watching it there would report a change whenever the ERP's ordered
 * quantity had drifted since the log was written.
 */
export function lineWasEdited(baseline: EditableLineFields | undefined, edited: EditableLineFields): boolean {
  if (!baseline) { return true; }
  if (
    (baseline.status ?? null) !== (edited.status ?? null) ||
    String(baseline.receivedQty ?? '').trim() !== String(edited.receivedQty ?? '').trim() ||
    String(baseline.partsReceived ?? '').trim() !== String(edited.partsReceived ?? '').trim() ||
    !!baseline.completesLine !== !!edited.completesLine
  ) {
    return true;
  }
  return !!edited.isOffPo
    && String(baseline.quantity ?? '').trim() !== String(edited.quantity ?? '').trim();
}

/**
 * Reconciles the edited check-off against the log's stored receipt rows.
 *
 * @param loadedRows THIS log's receipt rows, verbatim from the details payload.
 *   Payload order is significant — it is how off-PO rows are paired back to their
 *   seeded rows (see below).
 * @param baselineLines The check-off exactly as seeded, BEFORE any edit. A line
 *   that still matches its baseline is skipped entirely — see the header for the
 *   two-logs-one-line case that makes this necessary rather than tidy.
 * @param editedLines The check-off rows after the user's edits, PO lines and
 *   off-PO rows together. Off-PO rows must carry the negative `line` numbering
 *   `seedOffPoRows` assigns (`-1` for the first off-PO row in payload order, `-2`
 *   for the second, …) — that numbering is the ONLY link back to a stored off-PO
 *   row, because an off-PO row has no line sequence to group by.
 * @param damageByLine The damage seeded from those same rows. Fed to the builder
 *   so target quantities match stored ones; never emitted in a patch.
 */
export function planReceiptChanges(
  loadedRows: readonly ReceivingLogEditReceipt[] | null | undefined,
  baselineLines: readonly CheckableLineItem[] | null | undefined,
  editedLines: readonly CheckableLineItem[] | null | undefined,
  damageByLine: Record<string, LineDamage> = {},
): ReceiptPlan {
  const plan = emptyReceiptPlan();
  const stored = groupStoredRows(loadedRows);
  const baseline = byGroupKey(baselineLines);

  for (const line of editedLines ?? []) {
    const key = storedGroupKey(line);
    if (!lineWasEdited(baseline.get(key), line)) { continue; }

    const storedRows = stored.get(key) ?? [];

    // A canceled line has nothing to receive, so `buildReceiptRows` drops it and
    // the target would be empty — which would read as "delete every stored row on
    // it". The UI renders canceled lines read-only, but that is a UI promise, not
    // a data one, so refuse rather than trust it.
    if (line.isCanceled) {
      if (storedRows.length > 0) { plan.unsupported.push(canceledLineNote(line)); }
      continue;
    }

    reconcileGroup(plan, line, storedRows, buildReceiptRows([line], damageByLine));
  }

  // An OFF-PO group the edited set no longer mentions really was removed.
  //
  // A PO-LINE group absent from the edited set is left alone, because the check-off is
  // built from the PO's own lines and a line can be missing from it for a reason that
  // has nothing to do with the user — a degraded PO read, a line since removed from the
  // order. Reading that as "delete these arrivals" would destroy ledger rows over a
  // failed fetch.
  //
  // Off-PO rows carry none of that risk: they come from the LOG's own receipts, never
  // from the PO read, so the edited set always started with every one of them. If one
  // is gone, a person took it off.
  const mentioned = new Set((editedLines ?? []).map(storedGroupKey));
  stored.forEach((rows, key) => {
    if (mentioned.has(key) || Number(key) >= 0) { return; }
    for (const row of rows) {
      if (row.isDamaged) {
        // Same refusal as anywhere else: a delete is rejected when damage photos are
        // attached, and it discards a report this screen cannot re-send.
        plan.unsupported.push(removedDamagedRowNote(row));
        continue;
      }
      plan.deletedReceiptIds.push(row.id);
    }
  });

  plan.unsupported = dedupe(plan.unsupported);
  return plan;
}

/**
 * Reconciles one line's target rows against its stored rows, pairing in order.
 *
 * Order is the payload's own for the stored side and the builder's for the target
 * side, and both list a ragged line's parts in the order they were entered — so
 * pairing by position keeps a part with its own row instead of shuffling
 * descriptions between ids.
 */
function reconcileGroup(
  plan: ReceiptPlan,
  line: CheckableLineItem,
  storedRows: readonly ReceivingLogEditReceipt[],
  targetRows: readonly ReceiptSubmitRow[],
): void {
  const ragged = isRaggedForEdit(line);
  const pairs = Math.min(storedRows.length, targetRows.length);

  for (let i = 0; i < pairs; i++) {
    applyPair(plan, line, storedRows[i], targetRows[i], ragged);
  }

  // Surplus stored rows: the line now needs fewer than it has (a ragged line lost
  // a part, or the arrival was cleared entirely).
  for (let i = pairs; i < storedRows.length; i++) {
    const row = storedRows[i];
    if (row.isDamaged) {
      // Deleting it is refused when it has damage photos, and discards a damage
      // report this screen cannot re-send even when it is not.
      plan.unsupported.push(damagedRowNote(line, 'removed'));
      continue;
    }
    plan.deletedReceiptIds.push(row.id);
  }

  // Surplus target rows: the line arrived where it had not before, or gained parts.
  for (let i = pairs; i < targetRows.length; i++) {
    plan.addedReceipts.push(targetRows[i]);
  }
}

/**
 * Diffs one stored row against its target. Emits a patch when every difference is
 * expressible; replaces the row when a difference needs a null-clear; reports it
 * when the row is damaged and therefore cannot be replaced.
 */
function applyPair(
  plan: ReceiptPlan,
  line: CheckableLineItem,
  storedRow: ReceivingLogEditReceipt,
  target: ReceiptSubmitRow,
  ragged: boolean,
): void {
  const edits: ReceiptRowEdits = {};
  let needsNullClear = false;

  if (target.quantityReceived !== storedRow.quantityReceived) {
    if (target.quantityReceived == null) {
      needsNullClear = true;
    } else {
      edits.quantityReceived = target.quantityReceived;
    }
  }

  if (target.receiptStatus !== storedRow.receiptStatus) {
    if (target.receiptStatus == null) {
      needsNullClear = true;
    } else {
      edits.receiptStatus = target.receiptStatus;
    }
  }

  // A real decision either way. `false` on a closed row RE-OPENS the PO line, so
  // it is emitted only because the target genuinely differs from what is stored —
  // never echoed from a loaded row.
  if (target.completesLine !== storedRow.completesLine) {
    edits.completesLine = target.completesLine;
  }

  // Only on a ragged line, where the description is the part the user typed. On a
  // countable line it comes from the PO and diffing it would rewrite stored
  // history if the ERP's copy has drifted.
  if (ragged) {
    const description = (target.itemDescription ?? '').trim();
    if (description.length > 0 && description !== (storedRow.itemDescription ?? '')) {
      edits.itemDescription = description;
    }
  }

  if (needsNullClear) {
    if (storedRow.isDamaged) {
      plan.unsupported.push(damagedRowNote(line, 'changed'));
      return;
    }
    // A patch cannot write null; a delete plus an add can, and the backend applies
    // deletes before adds.
    plan.deletedReceiptIds.push(storedRow.id);
    plan.addedReceipts.push(target);
    return;
  }

  if (Object.keys(edits).length > 0) {
    plan.receiptEdits.set(storedRow.id, edits);
  }
}

/**
 * Whether this line is ragged FOR THE PURPOSE OF THE EDIT — it already has part
 * rows, or the user has entered parts text this time. Same test
 * `buildCompleteRows` applies, so the two agree on how many rows the line needs.
 *
 * File-local. There was an exported `isRaggedEditLine` wrapper here for a UI caller
 * that never arrived — the check-off decides for itself whether to render a quantity
 * input or a parts field — and an export with no caller is a claim about how the
 * screen works that nothing keeps honest.
 */
function isRaggedForEdit(line: CheckableLineItem): boolean {
  return isRaggedLine(line.receipts) || splitRaggedParts(line.partsReceived).length > 0;
}

/**
 * Stored rows grouped the way the check-off addresses them: by PO line sequence,
 * and off-PO rows by the negative synthetic line `seedOffPoRows` hands out in
 * payload order.
 */
function groupStoredRows(
  loadedRows: readonly ReceivingLogEditReceipt[] | null | undefined,
): Map<string, ReceivingLogEditReceipt[]> {
  const groups = new Map<string, ReceivingLogEditReceipt[]>();
  let offPoLine = 0;

  for (const row of loadedRows ?? []) {
    if (row.poLineSequence == null) {
      // One group per off-PO row: they never merge, and the nth off-PO row is
      // addressed as line -(n+1).
      offPoLine--;
      groups.set(String(offPoLine), [row]);
      continue;
    }
    const key = String(row.poLineSequence);
    const existing = groups.get(key);
    if (existing) { existing.push(row); } else { groups.set(key, [row]); }
  }

  return groups;
}

/** Check-off rows keyed the same way the stored groups are, so a baseline can be looked up per line. */
function byGroupKey(lines: readonly CheckableLineItem[] | null | undefined): Map<string, CheckableLineItem> {
  const map = new Map<string, CheckableLineItem>();
  for (const line of lines ?? []) { map.set(storedGroupKey(line), line); }
  return map;
}

/** The stored group an edited row addresses. Off-PO rows use their negative `line`; PO lines their sequence. */
function storedGroupKey(line: CheckableLineItem): string {
  return line.poLineSequence != null ? String(line.poLineSequence) : String(line.line);
}

function removedDamagedRowNote(row: ReceivingLogEditReceipt): string {
  const label = row.itemDescription ? `"${row.itemDescription}"` : 'An off-PO item';
  return `${label} has a damage report on it, so it cannot be removed here. `
    + `Correct the damage first, or leave the item on the log.`;
}

function damagedRowNote(line: CheckableLineItem, verb: 'changed' | 'removed'): string {
  return `${lineLabel(line)} has a damage report on it, so its receipt row cannot be ${verb} here. `
    + `Correct the damage first, or leave this line as it is.`;
}

function canceledLineNote(line: CheckableLineItem): string {
  return `${lineLabel(line)} is canceled on the PO but has receipts on this log, so it was left exactly as it is.`;
}

function lineLabel(line: CheckableLineItem): string {
  if (line.poLineSequence != null) { return `Line ${line.poLineSequence}`; }
  return line.description ? `"${line.description}"` : 'An off-PO item';
}

function dedupe(values: readonly string[]): string[] {
  return [...new Set(values)];
}
